// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import { err, ok, type Result } from "neverthrow";

export interface ArtifactListItem {
  id: string;
  title: string;
  description: string;
  toolId: string;
  contentType: string;
  chatId: string;
  messageId: string;
  createdAt: string;
  signedUrl: string;
  expiresAt: string;
}

export interface ListArtifactsResponse {
  artifacts: ArtifactListItem[];
}

interface ArtifactsServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
}

export interface ArtifactsService {
  listByChatId(chatId: string): Promise<Result<ListArtifactsResponse, Error>>;
}

export function createArtifactsService(config: ArtifactsServiceConfig): ArtifactsService {
  const getHeaders = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = config.authToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const projectId = config.projectId();
    if (projectId) {
      headers["X-Project-Id"] = projectId;
    }
    return headers;
  };

  const apiCall = async <T>(method: string, path: string): Promise<Result<T, Error>> => {
    if (!config.authReady()) {
      return err(new Error("Auth not ready"));
    }

    try {
      const response = await fetch(`/api${path}`, {
        method,
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData: unknown = await response.json().catch(() => ({}));
        const message: unknown =
          typeof errorData === "object" && errorData !== null ? Reflect.get(errorData, "message") : undefined;
        return err(new Error(typeof message === "string" && message ? message : `HTTP ${response.status}`));
      }

      const data: unknown = await response.json();
      return ok(data as T);
    } catch (error) {
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return {
    listByChatId: (chatId) => apiCall<ListArtifactsResponse>("GET", `/artifacts?chatId=${encodeURIComponent(chatId)}`),
  };
}
